/* ============================================================================
 * Project        : Deloitte Digital Compliance
 * File           : AdminSettings.jsx
 * Version        : 1.0.0
 * Author         : Team Neubinary
 * Created On     : 2026-06-24
 *
 * Description    : Digital Compliance for Automation and Governance
 *
 * Change History :
 * ----------------------------------------------------------------------------
 * Date         | Version | Author           | Description
 * ----------------------------------------------------------------------------
 * 2026-06-24   | 1.0.0   | Team Neubinary   | Initial creation
 * <YYYY-MM-DD> | x.x.x   | <Name/Team>      | <Change description>
 * ============================================================================
 *
 * Copyright (c) 2026 AU79 Consulting Services Pvt. Ltd.
 * All Rights Reserved.
 *
 * This source code and its contents are confidential and proprietary to
 * AU79 Consulting Services Pvt. Ltd. Unauthorized copying, reproduction,
 * modification, distribution, or disclosure of this material, in whole or
 * in part, is strictly prohibited without prior written permission from
 * AU79 Consulting Services Pvt. Ltd.
 * ============================================================================
 */
/**
 * @author Abdul Rahim M - abdul@au79consulting.com
 * @date 30-06-2024
 * @description Component for handling the AdminSettings.
 */

import React from "react";
import {
  Box,
  Paper,
  List,
  ListItemButton,
  ListItemText,
  Typography,
} from "@mui/material";

import { useDispatch, useSelector } from "react-redux";
import PowerTrainClass from "./PowerTrainClass";
import VehicleFunction from "./VehicleFunction";
import VehicleClass from "./VehicleClass";
import ArchitectureTypes from "./ArchitectureTypes";
import { HandleApiActions } from "../../redux/actions/actionHandler";
import adminSettingsAPI from "../../redux/actions/adminSetttingAPI";
import customConsole from "../../config/customConsole";
import VehicleStatus from "./VehicleStatus";
import VehicleSystems from "./VehicleSystems";
import SupplierMarkets from "./SupplierMarkets";
import NetworkInterfaces from "./NetworkInterfaces";
import Integration from "../integrations/Integration";
import { useLocation } from "react-router-dom";
import ReportTemplatesPage from "./Reports/ReportTemplatesPage";
import DynamicFormTemplatesPage from "./DynamicForms/DynamicFormTemplatesPage";
import Environments from "./Environments";

function AdminSettings() {
  // 1. Redux State Management
  const dispatch = useDispatch();
  const adminSettingsDetails = useSelector(
    (state) => state.adminSettingsReducer,
  );
  const location = useLocation();
  const queryParams = React.useMemo(() => {
    return new URLSearchParams(location.search);
  }, [location.search]);
  React.useEffect(() => {
    const hasFormParam = queryParams.has("form");

    if (hasFormParam) {
      setSelectedMenuItem("forms-templates");
    }
  }, [queryParams]);
  //2.React useState
  const [open, setOpen] = React.useState(null);
  // 2. Component State
  const [selectedMenuItem, setSelectedMenuItem] =
    React.useState("architecture");
  const [architectureDetails, setArchitectureDetails] = React.useState([]);
  const [zoneDetails, setZoneDetails] = React.useState([]);

  // 3. Menu Configuration
  const menuItems = [
    {
      id: "architecture",
      label: "Architecture & Delivery Market",
      component: "ArchitectureTypes",
    },
    {
      id: "supplier",
      label: "Supplier & Manufacturer",
      component: "ManufacturerDetails",
    },
    {
      id: "powertrain",
      label: "Powertrain Class",
      component: "PowerTrainClass",
    },
    {
      id: "vehicle-classes",
      label: "Vehicle Class",
      component: "VehicleClass",
    },
    {
      id: "vehicle-function",
      label: "Vehicle Function",
      component: "VehicleFunction",
    },
    {
      id: "vehicle-status",
      label: "Project Development Milestone",
      component: "VehicleStatus",
    },
    {
      id: "vehicle-system",
      label: "Vehicle System",
      component: "VehicleSystems",
    },
    {
      id: "forms-templates",
      label: "Regulatory Template",
      component: "FormsTemplates",
    },
    {
      id: "report-templates",
      label: "Reporting Templates",
      component: "ReportTemplatesPage",
    },
    {
      id: "network-interfaces",
      label: "Network Interface",
      component: "NetworkInterfaces",
    },
    {
      id: "integration",
      label: "Integration",
      component: "Integration",
    },
    {
      id: "environments",
      label: "Environment",
      component: "Environments",
    },
  ];

  // 4. Effect Hooks
  React.useEffect(() => {
    dispatch(
      HandleApiActions({
        ...adminSettingsAPI.get_admin_settings,
        show_toast: false,
      }),
    );
  }, []);

  //setState for Architecture Details and Zone Details
  React.useEffect(() => {
    customConsole("Admin Settings Details");
    customConsole(adminSettingsDetails);
    if (adminSettingsDetails?.adminSettings?.data?.architecture_types) {
      setArchitectureDetails(
        adminSettingsDetails.adminSettings.data.architecture_types,
      );
    }

    if (adminSettingsDetails?.adminSettings?.data?.zone_details) {
      setZoneDetails(adminSettingsDetails.adminSettings.data.zone_details);
    }
  }, [adminSettingsDetails]);

  // 5. Event Handlers
  const handleMenuItemClick = (menuId) => {
    setSelectedMenuItem(menuId);
  };

  const handleClick = (value) => {
    setOpen(open === value ? null : value);
  };

  // 6. Render Content based on selected menu
  const renderContent = () => {
    switch (selectedMenuItem) {
      case "architecture":
        return (
          <ArchitectureTypes
            open={selectedMenuItem}
            handleClick={handleClick}
            architectureDetails={architectureDetails}
            zoneDetails={zoneDetails}
          />
        );
      case "supplier":
        return (
          <SupplierMarkets open={selectedMenuItem} handleClick={handleClick} />
        );
      case "powertrain":
        return (
          <PowerTrainClass open={selectedMenuItem} handleClick={handleClick} />
        );
      case "vehicle-classes":
        return (
          <VehicleClass open={selectedMenuItem} handleClick={handleClick} />
        );
      case "vehicle-function":
        return (
          <VehicleFunction open={selectedMenuItem} handleClick={handleClick} />
        );
      case "vehicle-status":
        return (
          <VehicleStatus open={selectedMenuItem} handleClick={handleClick} />
        );
      case "vehicle-system":
        return (
          <VehicleSystems open={selectedMenuItem} handleClick={handleClick} />
        );
      case "forms-templates":
        return (
          <DynamicFormTemplatesPage
            open={selectedMenuItem}
            handleClick={handleClick}
          />
        );

      case "report-templates":
        return (
          <ReportTemplatesPage
            open={selectedMenuItem}
            handleClick={handleClick}
          />
        );

      case "network-interfaces":
        return (
          <NetworkInterfaces
            open={selectedMenuItem}
            handleClick={handleClick}
          />
        );
      case "integration":
        return (
          <Integration open={selectedMenuItem} handleClick={handleClick} />
        );

      case "environments":
        return (
          <Environments open={selectedMenuItem} handleClick={handleClick} />
        );
      default:
        return null;
    }
  };

  // 7. Main Render
  return (
    <Box
      sx={{
        ...styled.container,
        height: "100%",
        display: "flex",
        flexDirection: "column",
      }}
      data-testid="AdminSettings"
    >
      <div style={{ display: "flex", height: "100%", margin: 0, padding: 0 }}>
        {/* Left Sidebar */}
        <div style={{ width: "250px", flexShrink: 0, margin: 0, padding: 0 }}>
          <Paper sx={styled.sidebar}>
            <List component="nav" sx={{ padding: 0 }}>
              {menuItems.map((item) => (
                <React.Fragment key={item.id}>
                  <ListItemButton
                    onClick={() => handleMenuItemClick(item.id)}
                    sx={{
                      ...styled.menuItem,
                      backgroundColor:
                        selectedMenuItem === item.id
                          ? "#FFFFFF"
                          : "transparent",
                      color:
                        selectedMenuItem === item.id ? "#000000" : "#FFFFFF",
                      "&:hover": {
                        backgroundColor: "#8888887c",
                        color: "#FFFFFF",
                        "& .MuiTypography-root": {
                          color: "#FFFFFF",
                        },
                      },
                    }}
                  >
                    <ListItemText
                      primary={
                        <Box sx={styled.menuItemContent}>
                          <Typography
                            sx={{
                              ...styled.menuText,
                              color:
                                selectedMenuItem === item.id
                                  ? "#000000"
                                  : "#FFFFFF",
                            }}
                          >
                            {item.label}
                          </Typography>
                        </Box>
                      }
                    />
                  </ListItemButton>
                </React.Fragment>
              ))}
            </List>
          </Paper>
        </div>

        {/* Right Content Area */}
        <div style={{ flex: 1, margin: 0, padding: 0, height: "100%" }}>
          <Box sx={styled.contentArea}>{renderContent()}</Box>
        </div>
      </div>
    </Box>
  );
}

export default AdminSettings;

const styled = {
  container: {
    height: "100%",
    overflow: "hidden",
  },
  sidebar: {
    height: "100%",
    width: "250px",
    backgroundColor: "#000000",
    color: "#FFFFFF",
    overflow: "auto",
    borderRadius: "0 !important",
    border: "none",
    boxShadow: "none",
    margin: 0,
    padding: 0,
  },
  menuItem: {
    color: "#FFFFFF",
    width: "250px",
    height: "42px",
    "&:hover": {
      backgroundColor: "#8888887c",
    },
  },
  menuItemContent: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  },
  menuText: {
    fontStyle: "Open Sans",
    fontSize: "14px",
    fontWeight: 400,
    lineHeight: "20px",
    letterSpacing: "0%",
  },
  contentArea: {
    height: "100%",
    flex: 1,
    overflow: "auto",
    backgroundColor: "#ffffff",
    padding: "0",
    scrollbarGutter: "stable",
  },
  defaultContent: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    textAlign: "center",
  },
};
